import { useContext, useMemo, useState } from "react";
import { useTable } from "react-table";
import { cleanObject, formatCurrency } from "../../../../helpers";
import primaryComponents from "../../../primaryComponents";
import styles from "./styles.module.scss";
import { ToastContext } from "../../../../hooks/context";
import { useQueryClient } from "react-query";
import { v4 as uuidv4 } from "uuid";
import { useUpdatePricingViaPOMutation } from "../../../../hooks/api/mutations";
import RadioButtonV2 from "../../../primaryComponents/RadioButtonV2";
import { useStoreListObject } from "../../../../hooks/useStoreListObject";

export const usePOPricingUpdate = ({
  POItems = [],
  PODetails = null,
  pricingTypes = [],
  closeModal = () => {},
}) => {
  const triggerToast = useContext(ToastContext);
  const queryClient = useQueryClient();
  const [pOFormItem, setPOFormItem] = useState(POItems);
  const { storeMap } = useStoreListObject();

  const { mutate: updatePricingViaPO, isLoading: isUpdatingPO } =
    useUpdatePricingViaPOMutation();

  // Fixed columns
  const fixedColumns = useMemo(
    () => [
      {
        Header: "Product Name",
        accessor: "product_name",
      },
      {
        Header: "Store",
        accessor: "store_id",
        Cell: ({ cell: { value } }) => storeMap[value]?.name || "N/A",
      },
      {
        Header: "Unit",
        accessor: "unit",
        Cell: ({ cell: { value } }) => value || "N/A",
      },
      {
        Header: "Unit Cost Price",
        accessor: "cost_price",
        Cell: ({ cell: { value }, row }) =>
          formatCurrency({
            value,
            currencyCode: storeMap?.[row?.original?.store_id]?.currency_code,
          }),
      },
      {
        Header: "Use selling prices across other stores?",
        accessor: "apply_pricing_to_all_stores",
        Cell: ({ row }) => {
          const uuid = uuidv4();

          const handleRadioChange = (value) => {
            const newData = [...instance?.data];
            newData[row.index].apply_pricing_to_all_stores = value;
            setPOFormItem(newData);
          };

          return (
            <div
              className="d-flex justify-content-center align-items-center py-2"
              style={{
                gap: "4px",
                background: "#fff",
                borderRadius: "16px",
                minHeight: "15px",
                maxWidth: "140px",
              }}
            >
              <div
                className="d-flex align-items-center"
                style={{ gap: "12px" }}
              >
                <RadioButtonV2
                  name={`yesNo-BulkRestockData${uuid}`}
                  id={`yes-${row.index}`}
                  value={true}
                  checked={row.original?.apply_pricing_to_all_stores}
                  onChange={handleRadioChange}
                  label="Yes"
                />

                <div
                  style={{
                    width: "2px",
                    height: "20px",
                    backgroundColor: "#ccc",
                  }}
                ></div>

                {/* No Radio */}
                <RadioButtonV2
                  name={`yesNo-BulkRestockData${uuid}`}
                  id={`no-${row.index}`}
                  value={false}
                  checked={!row.original?.apply_pricing_to_all_stores}
                  onChange={handleRadioChange}
                  label="No"
                />
              </div>
            </div>
          );
        },
      },
    ],
    [],
  );

  // Dynamic pricing columns based on pricingTypes
  const dynamicColumns = useMemo(() => {
    return pricingTypes.map((pricingType) => ({
      Header: `Unit ${pricingType.label} Selling Price`,
      accessor: `pricing_${pricingType?.id}`,
      Cell: ({ cell: { value }, row }) => {
        // Handle nested price object: value?.price
        return value != null ? (
          <primaryComponents.InputField
            value={value?.price}
            classNames={`white ${styles.inputField}`}
            placeholder={"Enter selling price"}
            step={0.01}
            min={0.01}
            type="number"
            floatError={false}
            onChange={({ target: { value } }) => {
              const updatedItems = [...instance?.data];
              updatedItems[row.index][`pricing_${pricingType?.id}`].price =
                value;

              setPOFormItem(updatedItems);
            }}
          />
        ) : (
          "N/A"
        );
      },
    }));
  }, []);

  // Combined columns
  const columns = useMemo(
    () => [...fixedColumns, ...dynamicColumns],
    [fixedColumns, dynamicColumns],
  );

  // React Table instance
  const instance = useTable({
    columns,
    data: pOFormItem || [],
  });

  const handleUpdatePOSellingPrice = () => {
    const data = pOFormItem?.map((item) => {
      return cleanObject({
        product_id: item?.product_id,
        variant_id: item?.variant_id,
        store_id: item?.store_id,
        apply_pricing_to_all_stores: item?.apply_pricing_to_all_stores,
        pricings: Object.keys(item)
          .filter((key) => /^pricing_\d+$/.test(key)) // matches pricing_1, pricing_2, etc.
          .map((key) => item[key])
          .filter(
            (value) => value && typeof value === "object" && "price" in value,
          ),
      });
    });

    updatePricingViaPO(
      { id: PODetails?.id, data: { items: data } },
      {
        onSuccess: () => {
          triggerToast("Selling prices updated successfully", "success");
          queryClient.invalidateQueries([
            "getPurchaseOrderDetails",
            `${PODetails?.id}`,
          ]);
          closeModal();
        },
      },
    );
  };

  return {
    isUpdatingPO,
    instance,
    handleUpdatePOSellingPrice,
  };
};
